import React, { useState } from "react";
import { PageHero, FadeUp } from "../../components/public/Section";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const s = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", department: "General" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true); toast.success("Message sent successfully");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div>
      <PageHero title="Contact Us" subtitle="We're happy to hear from members, prospective members and the public." breadcrumbs={["Home", "Contact"]} />
      <section className="section container-page grid grid-cols-1 lg:grid-cols-3 gap-8">
        <FadeUp className="space-y-4">
          {[
            [MapPin, "Address", s.address],
            [Phone, "Phone", s.phone],
            [Mail, "Email", s.email],
          ].map(([Ico, l, v]) => (
            <div key={l} className="card-elev p-6 flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-brand-primary-light text-brand-primary flex items-center justify-center shrink-0"><Ico size={20} /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-muted font-semibold">{l}</div>
                <div className="font-semibold text-brand-text mt-1 break-all">{v}</div>
              </div>
            </div>
          ))}
          <div className="card-elev p-6 bg-brand-primary text-white">
            <div className="font-semibold">Departments</div>
            <ul className="mt-3 space-y-2 text-sm text-white/85">
              <li>General Enquiries — info@gosociety.org</li>
              <li>Membership — membership@gosociety.org</li>
              <li>Academic / CME — academic@gosociety.org</li>
            </ul>
          </div>
        </FadeUp>
        <FadeUp delay={0.15} className="lg:col-span-2 card-elev p-8">
          {done ? (
            <div className="text-center py-16">
              <CheckCircle2 size={56} className="text-brand-accent mx-auto" />
              <h3 className="font-serif text-2xl font-bold mt-4">Thank you!</h3>
              <p className="text-brand-muted mt-2">We've received your message and will get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div><label className="form-label">Name *</label><input required value={form.name} onChange={upd("name")} className="form-input" data-testid="contact-name" /></div>
              <div><label className="form-label">Email *</label><input required type="email" value={form.email} onChange={upd("email")} className="form-input" data-testid="contact-email" /></div>
              <div><label className="form-label">Phone</label><input value={form.phone} onChange={upd("phone")} className="form-input" /></div>
              <div><label className="form-label">Department</label><select value={form.department} onChange={upd("department")} className="form-input">{["General", "Membership", "Academic / CME", "Grievance"].map((d) => <option key={d}>{d}</option>)}</select></div>
              <div className="md:col-span-2"><label className="form-label">Subject *</label><input required value={form.subject} onChange={upd("subject")} className="form-input" data-testid="contact-subject" /></div>
              <div className="md:col-span-2"><label className="form-label">Message *</label><textarea required rows={5} value={form.message} onChange={upd("message")} className="form-input" data-testid="contact-message"></textarea></div>
              <div className="md:col-span-2"><button disabled={submitting} className="btn-primary" data-testid="contact-submit">{submitting ? "Sending…" : "Send Message"}</button></div>
            </form>
          )}
        </FadeUp>
      </section>
      {s.google_maps_embed && (
        <section className="pb-16 container-page">
          <div className="rounded-2xl overflow-hidden shadow-card border border-brand-border">
            <iframe title="map" src={s.google_maps_embed} className="w-full h-[420px]" allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
          </div>
        </section>
      )}
    </div>
  );
}
